import type {
  IMapFinish,
  IMapFinishGroup,
  IMapFinishScalar,
  IMapFinishSchema,
  IMapFinishVariant,
  IMapFinishVector,
  IMapLayoutCatalog,
  IMapLayoutFinish,
  IMapTextureSet,
} from '@interfaces/map';

import type { MapEditorCatalogElement } from './mapEditorCatalog';

// The finish registry as the menu reads it: normalised, every list present, nothing malformed left in.
//
// Parsed the same way the room catalog is — defensively per entry, so one bad scalar costs its own
// slider rather than the whole panel. This crosses a JSON file authored by hand alongside the kit
// and shipped through the build's metadata, which is exactly where a half-finished entry comes from.
export interface MapEditorFinishRegistry {
  schema: IMapFinishSchema | null;
  textureSets: IMapTextureSet[];
  finishes: IMapFinish[];
}

// A group and the controls inside it, in the order the menu renders them.
export interface MapEditorFinishGroupView {
  group: IMapFinishGroup;
  scalars: IMapFinishScalar[];
  vectors: IMapFinishVector[];
}

// The order semantic slots are offered in. Anything the kit names outside this list keeps its own
// alphabetical order behind them rather than being dropped: a kit may classify a surface this hub
// version has never heard of, and an unknown slot the author can still finish beats a hidden one.
const SEMANTIC_SLOT_ORDER = ['wall', 'floor', 'ceiling', 'trim'];

// Texture roles a set is addressed by. Closed on purpose — the menu keys a set's completeness off
// this, and the schema's `textures` declare exactly these four.
export const TEXTURE_ROLES = [
  'albedo',
  'normal',
  'height',
  'roughness',
] as const;

export type TextureRole = (typeof TEXTURE_ROLES)[number];

const EMPTY_REGISTRY: MapEditorFinishRegistry = {
  schema: null,
  textureSets: [],
  finishes: [],
};

/**
 * Reads a number that has to be one.
 *
 * @param value - Candidate straight out of the catalog JSON.
 * @param fallback - Returned when the candidate is absent or not finite.
 * @returns A usable number.
 */
const readNumber = (value: unknown, fallback: number): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

/**
 * Reads a trimmed string.
 *
 * @param value - Candidate straight out of the catalog JSON.
 * @returns The trimmed string, or empty when there was none.
 */
const readString = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

/**
 * Normalises one scalar declaration.
 *
 * Bounds are repaired rather than trusted, because an inverted or absent pair would otherwise reach
 * a slider as `min > max`, which MUI renders as a control that cannot be moved. A step of zero is
 * the same class of problem and is replaced by a hundredth of the range.
 *
 * @param raw - The declaration as shipped.
 * @returns The scalar, or null when it carries no key or parameter to address.
 */
const parseScalar = (raw: unknown): IMapFinishScalar | null => {
  const entry = raw as Partial<IMapFinishScalar> | null;
  const key = readString(entry?.key);
  const param = readString(entry?.param);
  if (!key || !param) {
    return null;
  }

  const lo = readNumber(entry?.min, 0);
  const hi = readNumber(entry?.max, 1);
  const min = Math.min(lo, hi);
  const max = Math.max(lo, hi);
  const step =
    Math.abs(readNumber(entry?.step, 0)) || (max - min) / 100 || 0.01;

  return {
    key,
    param,
    group: readString(entry?.group),
    displayName: readString(entry?.displayName) || key,
    default: clampToRange(readNumber(entry?.default, min), min, max),
    min,
    max,
    step,
    ...(entry?.perfCritical === true ? { perfCritical: true as const } : {}),
    ...(readString(entry?.unit) ? { unit: readString(entry?.unit) } : {}),
    ...(readString(entry?.mustNotExceed)
      ? { mustNotExceed: readString(entry?.mustNotExceed) }
      : {}),
    ...(readString(entry?.description)
      ? { description: readString(entry?.description) }
      : {}),
  };
};

/**
 * Normalises one vector declaration.
 *
 * @param raw - The declaration as shipped.
 * @returns The vector with an RGBA default, or null when it carries no key or parameter.
 */
const parseVector = (raw: unknown): IMapFinishVector | null => {
  const entry = raw as Partial<IMapFinishVector> | null;
  const key = readString(entry?.key);
  const param = readString(entry?.param);
  if (!key || !param) {
    return null;
  }

  return {
    key,
    param,
    group: readString(entry?.group),
    displayName: readString(entry?.displayName) || key,
    default: readRgba(entry?.default),
    ...(readString(entry?.description)
      ? { description: readString(entry?.description) }
      : {}),
  };
};

/**
 * Reads an RGBA quadruple.
 *
 * Padded to four channels and clamped to 0..1, so a three-channel colour or an out-of-range one
 * still produces something a colour picker can hold.
 *
 * @param value - Candidate array.
 * @returns Four channels, each 0..1.
 */
export const readRgba = (value: unknown): number[] => {
  const raw = Array.isArray(value) ? value : [];
  const channels = [0, 1, 2, 3].map((index) =>
    clampToRange(readNumber(raw[index], index === 3 ? 1 : 1), 0, 1),
  );

  return channels;
};

/**
 * Clamps a number into a range.
 *
 * @param value - Candidate.
 * @param min - Lower bound, inclusive.
 * @param max - Upper bound, inclusive.
 * @returns The value, moved inside the range when it fell outside it.
 */
export const clampToRange = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

/**
 * The highest a scalar may actually go, given what the rest of the finish is set to.
 *
 * A scalar naming another in `mustNotExceed` has a moving ceiling: its declared max still applies,
 * and so does the current value of the scalar it defers to, whichever is lower. Resolved here rather
 * than at each control so the slider, the number field and the resolve path cannot disagree — and so
 * the ceiling moves the moment the author raises the parameter it depends on.
 *
 * A `mustNotExceed` naming a key the schema does not declare is ignored rather than treated as zero:
 * a build may name a scalar this hub's schema copy has not got, and refusing to move the control at
 * all would be the worse failure.
 *
 * @param scalar - Declaration to bound.
 * @param scalars - The resolved scalar values of the same finish.
 * @param schema - The schema, for reading the referenced scalar's own bounds.
 * @returns The effective upper bound.
 */
export const effectiveMaxFor = (
  scalar: IMapFinishScalar,
  scalars: Record<string, number>,
  schema: IMapFinishSchema | null,
): number => {
  if (!scalar.mustNotExceed) {
    return scalar.max;
  }

  const ceiling = scalars[scalar.mustNotExceed];
  const declared = schema?.scalars.some(
    (candidate) => candidate.key === scalar.mustNotExceed,
  );
  if (!declared || typeof ceiling !== 'number' || !Number.isFinite(ceiling)) {
    return scalar.max;
  }

  return Math.min(scalar.max, ceiling);
};

/**
 * Clamps a value against the bounds its own scalar declares.
 *
 * The schema's bounds are the only clamp in this generation of the contract, so this is where a
 * perf-critical parameter is actually held down. UE clamps again on the way in — a client can send
 * anything — but nothing echoes the clamped value back for a preview, so a slider that let the
 * author past the bound would disagree with the world and never be corrected.
 *
 * @param scalar - Declaration carrying the bounds.
 * @param value - Candidate value.
 * @returns The value inside the declared range.
 */
export const clampScalar = (scalar: IMapFinishScalar, value: number): number =>
  clampToRange(readNumber(value, scalar.default), scalar.min, scalar.max);

/**
 * Reads the finish registry off a room catalog.
 *
 * Absent sections are not an error and not a partial state: a build that ships a kit without
 * finishes has no finish menu, which is the normal case for every build made before this feature.
 * The schema is the gate — without it there is nothing to render a control from, so the presets and
 * texture sets that might still be present are of no use on their own.
 *
 * @param catalog - The catalog this map's build shipped, or nothing.
 * @returns The registry, with every list normalised and empty where the build shipped none.
 */
export const resolveFinishRegistry = (
  catalog: IMapLayoutCatalog | null | undefined,
): MapEditorFinishRegistry => {
  const raw = catalog?.finishSchema;
  if (!raw || typeof raw !== 'object') {
    return EMPTY_REGISTRY;
  }

  const scalars = (Array.isArray(raw.scalars) ? raw.scalars : []).reduce<
    IMapFinishScalar[]
  >((parsed, entry) => {
    const scalar = parseScalar(entry);
    return scalar ? [...parsed, scalar] : parsed;
  }, []);

  const vectors = (Array.isArray(raw.vectors) ? raw.vectors : []).reduce<
    IMapFinishVector[]
  >((parsed, entry) => {
    const vector = parseVector(entry);
    return vector ? [...parsed, vector] : parsed;
  }, []);

  const variants = (Array.isArray(raw.variants) ? raw.variants : []).reduce<
    IMapFinishVariant[]
  >((parsed, entry) => {
    const variant = (entry ?? {}) as Partial<IMapFinishVariant>;
    const id = readString(variant.id);
    if (!id) {
      return parsed;
    }

    const requiredRoles = Array.isArray(variant.requiredRoles)
      ? variant.requiredRoles.map(readString).filter(Boolean)
      : [];

    return [
      ...parsed,
      {
        id,
        displayName: readString(variant.displayName) || id,
        ...(variant.staticSwitches
          ? { staticSwitches: variant.staticSwitches }
          : {}),
        ...(requiredRoles.length ? { requiredRoles } : {}),
      },
    ];
  }, []);

  const groups = (Array.isArray(raw.groups) ? raw.groups : []).reduce<
    IMapFinishGroup[]
  >((parsed, entry) => {
    const group = entry as Partial<IMapFinishGroup> | null;
    const id = readString(group?.id);

    return id
      ? [...parsed, { id, displayName: readString(group?.displayName) || id }]
      : parsed;
  }, []);

  // Nothing to move and nothing to colour means no menu, whatever else the build shipped.
  if (!scalars.length && !vectors.length) {
    return EMPTY_REGISTRY;
  }

  return {
    schema: {
      master: readString(raw.master),
      variants,
      groups,
      scalars,
      vectors,
      textures: Array.isArray(raw.textures) ? raw.textures : [],
    },
    textureSets: parseTextureSets(catalog?.textureSets),
    finishes: parseFinishes(catalog?.finishes),
  };
};

/**
 * Normalises the texture-set registry.
 *
 * @param raw - The catalog's `textureSets`, or whatever was found in its place.
 * @returns The sets that carry an id, each with only the roles it actually supplies.
 */
const parseTextureSets = (raw: unknown): IMapTextureSet[] => {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw.reduce<IMapTextureSet[]>((parsed, entry) => {
    const set = entry as Partial<IMapTextureSet> | null;
    const id = readString(set?.id);
    if (!id) {
      return parsed;
    }

    const assets = (set ?? {}) as unknown as Record<string, unknown>;
    const roles = TEXTURE_ROLES.reduce<Partial<IMapTextureSet>>(
      (carried, role) => {
        const asset = readString(assets[role]);

        return asset ? { ...carried, [role]: asset } : carried;
      },
      {},
    );

    return [
      ...parsed,
      { id, displayName: readString(set?.displayName) || id, ...roles },
    ];
  }, []);
};

/**
 * Normalises the preset registry.
 *
 * A preset's `scalars`/`vectors` stay sparse here — they are a diff from the schema default, and
 * flattening them at read time would freeze the master's defaults into every preset. Resolution
 * happens once, in buildFinishEntry, at the moment the author picks one.
 *
 * @param raw - The catalog's `finishes`, or whatever was found in its place.
 * @returns The presets that name an id, a variant and a texture set.
 */
const parseFinishes = (raw: unknown): IMapFinish[] => {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw.reduce<IMapFinish[]>((parsed, entry) => {
    const finish = entry as Partial<IMapFinish> | null;
    const id = readString(finish?.id);
    const variant = readString(finish?.variant);
    const textureSet = readString(finish?.textureSet);
    if (!id || !variant || !textureSet) {
      return parsed;
    }

    return [
      ...parsed,
      {
        id,
        displayName: readString(finish?.displayName) || id,
        thumbnailUrl: readString(finish?.thumbnailUrl),
        variant,
        textureSet,
        scalars: readScalarMap(finish?.scalars),
        vectors: readVectorMap(finish?.vectors),
      },
    ];
  }, []);
};

/**
 * Reads a sparse map of scalar overrides.
 *
 * @param raw - Candidate object.
 * @returns Only the entries whose value is a finite number.
 */
export const readScalarMap = (raw: unknown): Record<string, number> => {
  if (!raw || typeof raw !== 'object') {
    return {};
  }

  return Object.entries(raw as Record<string, unknown>).reduce<
    Record<string, number>
  >((parsed, [key, value]) => {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      return parsed;
    }

    return { ...parsed, [key]: value };
  }, {});
};

/**
 * Reads a sparse map of vector overrides.
 *
 * @param raw - Candidate object.
 * @returns Only the entries that hold an array, each padded and clamped to RGBA.
 */
export const readVectorMap = (raw: unknown): Record<string, number[]> => {
  if (!raw || typeof raw !== 'object') {
    return {};
  }

  return Object.entries(raw as Record<string, unknown>).reduce<
    Record<string, number[]>
  >((parsed, [key, value]) => {
    if (!Array.isArray(value)) {
      return parsed;
    }

    return { ...parsed, [key]: readRgba(value) };
  }, {});
};

/**
 * Reads a finish entry that arrived over the wire.
 *
 * Defensive per field, because this crosses a message from a build that may be newer than this hub:
 * an entry naming no slot is unusable and dropped, while unknown extra fields are simply not read.
 * Values are taken as reported rather than re-clamped — the whole reason the authority reports them
 * is that they are what it applied, and clamping them again here would hide the cut it made.
 *
 * @param raw - One element of a `finishes` array, straight off the socket.
 * @returns The finish, or null when it carries no slot.
 */
export const parseLayoutFinish = (raw: unknown): IMapLayoutFinish | null => {
  const entry = raw as Partial<IMapLayoutFinish> | null;
  const slot = typeof entry?.slot === 'string' ? entry.slot.trim() : '';
  if (!slot) {
    return null;
  }

  const finish = typeof entry?.finish === 'string' ? entry.finish.trim() : '';

  return {
    slot,
    ...(finish ? { finish } : {}),
    variant: typeof entry?.variant === 'string' ? entry.variant.trim() : '',
    textureSet:
      typeof entry?.textureSet === 'string' ? entry.textureSet.trim() : '',
    scalars: readScalarMap(entry?.scalars),
    vectors: readVectorMap(entry?.vectors),
  };
};

/**
 * Reads a `finishes` array that arrived over the wire.
 *
 * @param raw - The array, or whatever was found in its place.
 * @returns The usable entries; undefined when the field was absent altogether, which is a different
 * answer from a room reported with no finishes on it.
 */
export const parseLayoutFinishes = (
  raw: unknown,
): IMapLayoutFinish[] | undefined => {
  if (!Array.isArray(raw)) {
    return undefined;
  }

  return raw.reduce<IMapLayoutFinish[]>((parsed, entry) => {
    const finish = parseLayoutFinish(entry);

    return finish ? [...parsed, finish] : parsed;
  }, []);
};

/**
 * The semantic surfaces the rooms in a draft can be finished on.
 *
 * Read off the catalog elements rather than off a geometry report, and that is deliberate: UE does
 * not answer a preview spawn with CURRENT_GEOMETRY_LIST, so a room the author has just dropped into
 * an unpublished draft has no report to be described by. The element's list is a build-time
 * projection of the level's own finish map, written by the pass that seeds it, so it can answer for a
 * room that does not exist in the world yet.
 *
 * An unclassified surface contributes nothing, so a kit part-way through that pass offers a short
 * list rather than a wrong one — the kit's own material slot names are not a vocabulary and are never
 * fallen back on.
 *
 * @param elementIds - Catalog element ids in the draft.
 * @param elements - The kit, normalised.
 * @returns Semantic surface names, deduped, known ones first in a fixed order.
 */
export const finishableSlotsFor = (
  elementIds: string[],
  elements: MapEditorCatalogElement[],
): string[] => {
  const wanted = new Set(elementIds);
  const found = new Set<string>();

  elements.forEach((element) => {
    if (!wanted.has(element.id)) {
      return;
    }

    element.finishSlots.forEach((slot) => found.add(slot));
  });

  return [...found].sort(compareSemanticSlots);
};

/**
 * Orders two semantic slot names.
 *
 * @param a - One name.
 * @param b - The other.
 * @returns Negative when `a` comes first, following SEMANTIC_SLOT_ORDER and then alphabetically.
 */
const compareSemanticSlots = (a: string, b: string): number => {
  const left = SEMANTIC_SLOT_ORDER.indexOf(a);
  const right = SEMANTIC_SLOT_ORDER.indexOf(b);
  if (left !== -1 && right !== -1) {
    return left - right;
  }
  if (left !== -1) {
    return -1;
  }
  if (right !== -1) {
    return 1;
  }

  return a.localeCompare(b);
};

/**
 * Whether a texture set supplies everything a variant reads.
 *
 * The pairing matters because the variant is a cooked static-switch permutation: `roughness-map`
 * samples a roughness map, and a set without one leaves that sample reading an engine placeholder.
 * Nothing downstream reports this, so the menu is the only place it can be prevented.
 *
 * @param variant - The variant a finish uses, or nothing when it names one the schema lacks.
 * @param set - Candidate texture set.
 * @returns True when every required role is supplied.
 */
export const isTextureSetCompatible = (
  variant: IMapFinishVariant | null | undefined,
  set: IMapTextureSet,
): boolean => {
  // Indexed by role name because the roles are data: the schema declares which ones a variant reads,
  // and reading them off named fields would mean a new role could not be checked without a hub change.
  const assets = set as unknown as Record<string, unknown>;

  return (variant?.requiredRoles ?? []).every((role) => Boolean(assets[role]));
};

/**
 * The texture sets offerable for a variant.
 *
 * @param variantId - Variant the finish is on.
 * @param registry - The finish registry.
 * @returns Only the sets that supply every role the variant reads.
 */
export const compatibleTextureSets = (
  variantId: string,
  registry: MapEditorFinishRegistry,
): IMapTextureSet[] => {
  const variant = registry.schema?.variants.find(
    (candidate) => candidate.id === variantId,
  );

  return registry.textureSets.filter((set) =>
    isTextureSetCompatible(variant, set),
  );
};

/**
 * The controls grouped the way the menu renders them.
 *
 * Order comes from the arrays as authored — groups in the order the schema lists them, controls in
 * the order they appear inside it — which makes the JSON the single place the panel's layout is
 * decided. Controls naming a group the schema does not declare are collected into a trailing group
 * rather than dropped, so a build shipping a group this hub has never heard of still offers them.
 *
 * @param schema - The finish schema.
 * @returns Groups with their scalars and vectors, empty groups omitted.
 */
export const orderedFinishGroups = (
  schema: IMapFinishSchema | null,
): MapEditorFinishGroupView[] => {
  if (!schema) {
    return [];
  }

  const declared = schema.groups.map((group) => ({
    group,
    scalars: schema.scalars.filter((scalar) => scalar.group === group.id),
    vectors: schema.vectors.filter((vector) => vector.group === group.id),
  }));

  const known = new Set(schema.groups.map((group) => group.id));
  const orphanScalars = schema.scalars.filter(
    (scalar) => !known.has(scalar.group),
  );
  const orphanVectors = schema.vectors.filter(
    (vector) => !known.has(vector.group),
  );

  const orphans =
    orphanScalars.length || orphanVectors.length
      ? [
          {
            group: { id: '', displayName: '' },
            scalars: orphanScalars,
            vectors: orphanVectors,
          },
        ]
      : [];

  return [...declared, ...orphans].filter(
    (view) => view.scalars.length > 0 || view.vectors.length > 0,
  );
};

/**
 * Resolves a preset into every value the master takes.
 *
 * Three layers, in order: the schema's default, then the preset's sparse diff, then the author's
 * own edits. The result is complete rather than sparse — that is what gets published, so that what
 * the author saw survives a later change to the master's defaults.
 *
 * @param schema - The finish schema, which decides what a complete set of values even is.
 * @param preset - The preset to start from, or nothing to start from the schema's defaults.
 * @param override - The author's edits, sparse; unknown keys are ignored.
 * @returns Every scalar and vector the schema declares, clamped to its bounds.
 */
export const resolveFinishValues = (
  schema: IMapFinishSchema,
  preset?: IMapFinish | null,
  override?: {
    scalars?: Record<string, number>;
    vectors?: Record<string, number[]>;
  } | null,
): { scalars: Record<string, number>; vectors: Record<string, number[]> } => {
  const scalars = schema.scalars.reduce<Record<string, number>>(
    (carried, scalar) => {
      const fromPreset = preset?.scalars?.[scalar.key];
      const fromAuthor = override?.scalars?.[scalar.key];
      const value = fromAuthor ?? fromPreset ?? scalar.default;

      return { ...carried, [scalar.key]: clampScalar(scalar, value) };
    },
    {},
  );

  const vectors = schema.vectors.reduce<Record<string, number[]>>(
    (carried, vector) => {
      const fromPreset = preset?.vectors?.[vector.key];
      const fromAuthor = override?.vectors?.[vector.key];

      return {
        ...carried,
        [vector.key]: readRgba(fromAuthor ?? fromPreset ?? vector.default),
      };
    },
    {},
  );

  return { scalars: settleConstraints(schema, scalars), vectors };
};

/**
 * Brings every constrained scalar under the one it defers to.
 *
 * Applied over the whole resolved set rather than per edit, because that is where a violation can
 * come from without anybody having dragged anything: a preset may carry a pair the author never
 * touched, and the schema's defaults may be reduced in a later build. The constrained value comes
 * down and the ceiling is left where it is, matching what the authority does — so the value the hub
 * publishes is the value the authority will keep.
 *
 * @param schema - The finish schema.
 * @param scalars - Resolved values, complete.
 * @returns The same set with each constrained value at or below its ceiling.
 */
export const settleConstraints = (
  schema: IMapFinishSchema,
  scalars: Record<string, number>,
): Record<string, number> =>
  schema.scalars.reduce<Record<string, number>>(
    (settled, scalar) => ({
      ...settled,
      [scalar.key]: clampToRange(
        settled[scalar.key],
        scalar.min,
        effectiveMaxFor(scalar, settled, schema),
      ),
    }),
    scalars,
  );

/**
 * Builds the entry a slot is finished with, resolved and ready to publish.
 *
 * @param schema - The finish schema.
 * @param slot - Semantic slot this applies to.
 * @param preset - Preset the author picked; its id is carried as provenance.
 * @param override - Edits on top of the preset, sparse.
 * @returns The complete entry.
 */
export const buildFinishEntry = (
  schema: IMapFinishSchema,
  slot: string,
  preset: IMapFinish,
  override?: {
    variant?: string;
    textureSet?: string;
    scalars?: Record<string, number>;
    vectors?: Record<string, number[]>;
  } | null,
): IMapLayoutFinish => {
  const { scalars, vectors } = resolveFinishValues(schema, preset, override);

  return {
    slot,
    finish: preset.id,
    variant: override?.variant || preset.variant,
    textureSet: override?.textureSet || preset.textureSet,
    scalars,
    vectors,
  };
};

/**
 * The keys an entry has moved away from the preset it started at.
 *
 * What the panel's "modified" markers and its per-parameter reset are both driven by. Compared
 * against the preset resolved through the same schema, so a preset that leaves a parameter alone is
 * compared at the schema's default rather than at nothing.
 *
 * @param schema - The finish schema.
 * @param entry - The entry as edited.
 * @param preset - The preset it names, or nothing when the catalog no longer ships it.
 * @returns Keys of the scalars and vectors that differ.
 */
export const finishOverrideKeys = (
  schema: IMapFinishSchema,
  entry: IMapLayoutFinish,
  preset: IMapFinish | null | undefined,
): Set<string> => {
  if (!preset) {
    return new Set();
  }

  const base = resolveFinishValues(schema, preset);
  const changed = new Set<string>();

  Object.entries(entry.scalars).forEach(([key, value]) => {
    if (base.scalars[key] !== undefined && base.scalars[key] !== value) {
      changed.add(key);
    }
  });

  Object.entries(entry.vectors).forEach(([key, value]) => {
    const baseline = base.vectors[key];
    if (baseline && baseline.join(',') !== value.join(',')) {
      changed.add(key);
    }
  });

  return changed;
};

/**
 * Finds the finish standing on a slot.
 *
 * @param entries - Finishes of a scope.
 * @param slot - Semantic slot.
 * @returns The entry, or undefined when the slot is unfinished.
 */
export const findFinishForSlot = (
  entries: IMapLayoutFinish[],
  slot: string,
): IMapLayoutFinish | undefined => entries.find((entry) => entry.slot === slot);

/**
 * Replaces the finish on a slot, keeping the rest of the scope untouched.
 *
 * @param entries - Finishes of a scope.
 * @param entry - The finish to put on its slot.
 * @returns A new list with exactly one entry for that slot.
 */
export const upsertFinish = (
  entries: IMapLayoutFinish[],
  entry: IMapLayoutFinish,
): IMapLayoutFinish[] => {
  const others = entries.filter((candidate) => candidate.slot !== entry.slot);

  return [...others, entry];
};

/**
 * Takes the finish off a slot.
 *
 * Removal is not the same as finishing a slot with the kit's own material: nothing here can express
 * the authored material, so an unfinished slot is expressed by having no entry at all, and UE leaves
 * whatever the mesh shipped with in place.
 *
 * @param entries - Finishes of a scope.
 * @param slot - Semantic slot to clear.
 * @returns A new list without that slot.
 */
export const removeFinish = (
  entries: IMapLayoutFinish[],
  slot: string,
): IMapLayoutFinish[] => entries.filter((entry) => entry.slot !== slot);

/**
 * Whether two sets of finishes describe the same surfaces.
 *
 * Order-insensitive, because a scope's list is keyed by slot rather than ordered — upsertFinish
 * moves an edited entry to the end, and that is not a change the author made.
 *
 * @param a - One list.
 * @param b - The other.
 * @returns True when the same slots carry the same values.
 */
export const sameFinishes = (
  a: IMapLayoutFinish[],
  b: IMapLayoutFinish[],
): boolean => {
  if (a.length !== b.length) {
    return false;
  }

  const bySlot = new Map(b.map((entry) => [entry.slot, entry]));

  return a.every((entry) => {
    const other = bySlot.get(entry.slot);

    return (
      !!other &&
      other.finish === entry.finish &&
      other.variant === entry.variant &&
      other.textureSet === entry.textureSet &&
      JSON.stringify(other.scalars) === JSON.stringify(entry.scalars) &&
      JSON.stringify(other.vectors) === JSON.stringify(entry.vectors)
    );
  });
};

/**
 * The two or three parameters a preset actually changes, labelled for a tile with no thumbnail.
 *
 * Stands in for the artwork rather than faking it. A tint swatch was the obvious fallback and does
 * not survive contact with the shipped presets — both leave `tint` at the default, so a swatch
 * renders two identical white squares, which reads as a broken image rather than as a choice. The
 * preset's own diff from the schema is the honest identifying detail, and it is already authored.
 *
 * @param schema - The finish schema, which supplies the labels and units.
 * @param preset - The preset to describe.
 * @param limit - How many parameters to name.
 * @returns Short `label value` strings, in schema order.
 */
export const describeFinishDiff = (
  schema: IMapFinishSchema | null,
  preset: IMapFinish,
  limit = 3,
): string[] => {
  if (!schema) {
    return [];
  }

  return schema.scalars
    .filter((scalar) => preset.scalars?.[scalar.key] !== undefined)
    .slice(0, limit)
    .map((scalar) => {
      const value = preset.scalars?.[scalar.key] as number;
      const decimals = decimalsForStep(scalar.step);

      return `${scalar.displayName} ${value.toFixed(decimals)}${
        scalar.unit ?? ''
      }`;
    });
};

/**
 * How many decimals a value moved by this step needs.
 *
 * Derived rather than declared: a step of 0.05 is what says the control is worth showing to two
 * places, and a schema that also declared the precision could disagree with itself.
 *
 * @param step - The declared step.
 * @returns Decimal places, 0 to 3.
 */
export const decimalsForStep = (step: number): number => {
  if (!Number.isFinite(step) || step <= 0 || step >= 1) {
    return 0;
  }
  if (step >= 0.1) {
    return 1;
  }
  if (step >= 0.01) {
    return 2;
  }

  return 3;
};

/**
 * Formats a scalar for display.
 *
 * @param scalar - Declaration carrying the step and the unit.
 * @param value - Value to show.
 * @returns The number at the precision its step implies, with the unit appended.
 */
export const formatScalar = (scalar: IMapFinishScalar, value: number): string =>
  `${value.toFixed(decimalsForStep(scalar.step))}${scalar.unit ?? ''}`;

/**
 * An RGBA quadruple as a CSS colour.
 *
 * @param rgba - Four channels, each 0..1.
 * @returns An `rgb()` string; alpha is dropped, since it is a tint multiplier rather than opacity.
 */
export const rgbaToCss = (rgba: number[]): string => {
  const [r, g, b] = readRgba(rgba).map((channel) =>
    Math.round(clampToRange(channel, 0, 1) * 255),
  );

  return `rgb(${r}, ${g}, ${b})`;
};

/**
 * A CSS hex colour as an RGBA quadruple.
 *
 * @param hex - `#rrggbb`, with or without the hash.
 * @param alpha - Alpha to carry through, since a hex colour has none.
 * @returns Four channels, each 0..1.
 */
export const hexToRgba = (hex: string, alpha = 1): number[] => {
  const clean = hex.replace('#', '').trim();
  if (clean.length !== 6) {
    return [1, 1, 1, alpha];
  }

  const channels = [0, 2, 4].map(
    (offset) => parseInt(clean.slice(offset, offset + 2), 16) / 255,
  );

  return [...channels.map((channel) => clampToRange(channel, 0, 1)), alpha];
};

/**
 * An RGBA quadruple as a CSS hex colour, for a picker that only speaks hex.
 *
 * @param rgba - Four channels, each 0..1.
 * @returns `#rrggbb`.
 */
export const rgbaToHex = (rgba: number[]): string => {
  const [r, g, b] = readRgba(rgba).map((channel) =>
    Math.round(clampToRange(channel, 0, 1) * 255),
  );

  return `#${[r, g, b]
    .map((channel) => channel.toString(16).padStart(2, '0'))
    .join('')}`;
};
